import 'rxjs/add/operator/delayWhen';
import 'rxjs/operator/delay';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/takeUntil';
import { of } from 'rxjs/observable/of';
import { type ActionsObservable } from 'redux-observable';
import { timer } from 'rxjs/observable/timer';
import {
	CHECK_ACTIVATION_STATUS,
	CHECK_ACTIVATION_STATUS_ERROR,
	CHECK_ACTIVATION_STATUS_SUCCESS,
	checkConfluenceActivationStatus,
	checkConfluenceActivationStatusFailed,
	checkConfluenceActivationStatusSuccess,
} from '../actions/activate-confluence';
import { type Store } from 'redux';
import { type State } from '../types';
import { activationStatusRetryCount } from '../activation/selectors';
import { checkConfluenceActivationStatus$ } from './requests';
import { fetchFailed, fetchSuccess } from '../../common/fetch-request-analytics';
import { getCloudId } from '../context/selectors';

const MAX_RETRIES = 50;
const POLL_INTERVAL = 3000;
const INITIAL_DELAY = 6000;

export default (
	action$: ActionsObservable<ReturnType<typeof checkConfluenceActivationStatus>>,
	store: Store<State>,
) =>
	action$
		.ofType(CHECK_ACTIVATION_STATUS)
		.takeUntil(
			action$.ofType(CHECK_ACTIVATION_STATUS_ERROR) ||
				action$.ofType(CHECK_ACTIVATION_STATUS_SUCCESS),
		)
		.delayWhen(() => {
			const state = store.getState();
			const retryCount = activationStatusRetryCount(state);
			if (retryCount === 0) {
				return timer(INITIAL_DELAY);
			}
			if (retryCount % 2 === 0) {
				return timer(POLL_INTERVAL * 2);
			}
			return timer(POLL_INTERVAL);
		})
		.switchMap(() => {
			const state = store.getState();
			const cloudId: string | null | undefined = getCloudId(state);
			const retryCount = activationStatusRetryCount(state);

			if (retryCount > MAX_RETRIES) {
				return of(checkConfluenceActivationStatusFailed());
			}
			return checkConfluenceActivationStatus$(cloudId)
				.mergeMap((response) => {
					fetchSuccess('confluence.activate.status');
					switch (response.status) {
						case 'PROVISIONING_COMPLETED':
							return of(checkConfluenceActivationStatusSuccess());
						case 'PROVISIONING_FAILED':
							return of(checkConfluenceActivationStatusFailed());
						case 'PROVISIONING_IN_PROGRESS':
						default:
							return of(checkConfluenceActivationStatus());
					}
				})
				.catch((error) => {
					fetchFailed('confluence.activate.status', error);
					return of(checkConfluenceActivationStatusFailed());
				});
		});
